"use client";

import { motion, useReducedMotion } from "framer-motion";

export type THeroBackdropDepthLayerRGB = {
  r: number;
  g: number;
  b: number;
};

export type THeroBackdropDepthLayerColor = {
  rgb: THeroBackdropDepthLayerRGB;
  alpha: number;
};

export type THeroBackdropDepthLayerTheme = {
  /**
   * Large corner orb (top-right).
   * Example: rgba(79,142,110,0.26) represented as { rgb, alpha }.
   */
  orb1: THeroBackdropDepthLayerColor;
  /** Smaller orb (top-left). */
  orb2: THeroBackdropDepthLayerColor;
  /** Soft pool orb (bottom-center). */
  orb3: THeroBackdropDepthLayerColor;
  /**
   * Edge "fill" behind the orbs so left/right sides don't read as plain white.
   * This layer is intentionally subtle.
   */
  edgeFill: THeroBackdropDepthLayerColor;
};

export interface IHeroBackdropDepthLayerProps {
  /**
   * When enabled, the backdrop is rendered as a full-bleed strip horizontally
   * (to reach the viewport edges even if the hero content is max-width).
   */
  fullBleed?: boolean;
  /** Override the default (green) theme. */
  theme?: Partial<THeroBackdropDepthLayerTheme>;
  className?: string;
}

function rgba({ rgb, alpha }: THeroBackdropDepthLayerColor): string {
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

function mergeTheme(
  theme: Partial<THeroBackdropDepthLayerTheme> | undefined,
): THeroBackdropDepthLayerTheme {
  return {
    orb1: theme?.orb1 ?? { rgb: { r: 79, g: 142, b: 110 }, alpha: 0.26 },
    orb2: theme?.orb2 ?? { rgb: { r: 111, g: 180, b: 142 }, alpha: 0.2 },
    orb3: theme?.orb3 ?? { rgb: { r: 130, g: 195, b: 160 }, alpha: 0.18 },
    edgeFill:
      theme?.edgeFill ?? {
        rgb: { r: 79, g: 142, b: 110 },
        alpha: 0.14,
      },
  };
}

export function HeroBackdropDepthLayer({
  fullBleed = true,
  theme,
  className,
}: IHeroBackdropDepthLayerProps): React.ReactElement {
  const prefersReducedMotion = useReducedMotion();

  const resolvedTheme = mergeTheme(theme);

  const orb1Background = `radial-gradient(circle_at_center,${rgba(
    resolvedTheme.orb1,
  )}_0%,transparent_60%)`;
  const orb2Background = `radial-gradient(circle_at_center,${rgba(
    resolvedTheme.orb2,
  )}_0%,transparent_60%)`;
  const orb3Background = `radial-gradient(circle_at_center,${rgba(
    resolvedTheme.orb3,
  )}_0%,transparent_62%)`;

  const edgeFillBackground = `linear-gradient(90deg,rgba(${resolvedTheme.edgeFill.rgb.r},${resolvedTheme.edgeFill.rgb.g},${resolvedTheme.edgeFill.rgb.b},${
    resolvedTheme.edgeFill.alpha
  })_0%,transparent_22%,transparent_78%,rgba(${resolvedTheme.edgeFill.rgb.r},${resolvedTheme.edgeFill.rgb.g},${resolvedTheme.edgeFill.rgb.b},${
    resolvedTheme.edgeFill.alpha
  })_100%)`;

  const orb1Animate = prefersReducedMotion
    ? { opacity: 0.38 }
    : { x: [0, 16, 0], y: [0, -12, 0], opacity: [0.28, 0.6, 0.28] };
  const orb1Transition = prefersReducedMotion
    ? undefined
    : {
        duration: 22,
        repeat: Infinity,
        repeatType: "mirror" as const,
        ease: "easeInOut" as const,
      };

  const orb2Animate = prefersReducedMotion
    ? { opacity: 0.33 }
    : { x: [0, -12, 0], y: [0, 10, 0], opacity: [0.22, 0.52, 0.22] };
  const orb2Transition = prefersReducedMotion
    ? undefined
    : {
        duration: 26,
        repeat: Infinity,
        repeatType: "mirror" as const,
        ease: "easeInOut" as const,
      };

  const orb3Animate = prefersReducedMotion
    ? { opacity: 0.3 }
    : { x: [0, 10, 0], y: [0, 14, 0], opacity: [0.2, 0.46, 0.2] };
  const orb3Transition = prefersReducedMotion
    ? undefined
    : {
        duration: 30,
        repeat: Infinity,
        repeatType: "mirror" as const,
        ease: "easeInOut" as const,
      };

  const containerClassName = [
    "pointer-events-none absolute -z-10",
    fullBleed
      ? "inset-y-0 left-1/2 w-screen -translate-x-1/2"
      : "inset-0",
    // NOTE: This component shouldn't clip horizontally; otherwise the orbs may
    // read as floating islands with white on the sides.
    "overflow-visible",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      aria-hidden="true"
      className={containerClassName}
      style={{
        // Tailwind's arbitrary gradient syntax can't be parameterized, so we
        // use inline `backgroundImage` based on the provided theme.
        backgroundImage: edgeFillBackground.replaceAll("_", " "),
      }}
    >
      {/* Green depth orb (top-right) */}
      <motion.div
        aria-hidden="true"
        className="absolute -right-16 -top-20 h-[380px] w-[380px] rounded-full blur-3xl"
        animate={orb1Animate}
        {...(orb1Transition != null ? { transition: orb1Transition } : {})}
        style={{
          willChange: "transform, opacity",
          backgroundImage: orb1Background.replaceAll("_", " "),
        }}
      />

      {/* Lighter green depth orb (top-left) */}
      <motion.div
        aria-hidden="true"
        className="absolute -left-14 top-2 h-[320px] w-[320px] rounded-full blur-3xl"
        animate={orb2Animate}
        {...(orb2Transition != null ? { transition: orb2Transition } : {})}
        style={{
          willChange: "transform, opacity",
          backgroundImage: orb2Background.replaceAll("_", " "),
        }}
      />

      {/* Soft green pool (bottom-center) */}
      <motion.div
        aria-hidden="true"
        className="absolute left-1/2 bottom-[-200px] h-[460px] w-[460px] -translate-x-1/2 rounded-full blur-3xl"
        animate={orb3Animate}
        {...(orb3Transition != null ? { transition: orb3Transition } : {})}
        style={{
          willChange: "transform, opacity",
          backgroundImage: orb3Background.replaceAll("_", " "),
        }}
      />
    </div>
  );
}